<template>
  <div class="pickup-create-container">

    <!-- Compact Filters Section -->
    <div class="filters-panel-compact mb-3">
      <div class="filters-row">
        <div class="filter-item">
          <label class="filter-label-compact">Date</label>
          <b-form-datepicker
            v-model="date"
            v-bind="datePickerConfig"
            placeholder="Date d'expédition"
            reset-button
            close-button
            size="sm"
            class="filter-input-compact"
            @input="getOrders"
          />
        </div>

        <div class="filter-item filter-item-wide">
          <label class="filter-label-compact">Recherche</label>
          <b-form-input
            v-model="searchTerm"
            placeholder="N° bon, client, téléphone..."
            type="text"
            size="sm"
            class="filter-input-compact"
          />
        </div>

        <div class="filter-item">
          <label class="filter-label-compact">Gouvernorat</label>
          <v-select
            v-model="selectedGovernorate"
            :clearable="true"
            placeholder="Tous"
            label="name"
            :options="governorates"
            class="filter-select-compact"
            @input="filterByGovernorate"
          />
        </div>

        <div class="filter-item">
          <label class="filter-label-compact">Heure collecte</label>
          <b-form-input
            v-model="pickupDatetime"
            type="datetime-local"
            size="sm"
            class="filter-input-compact"
          />
        </div>

        <div class="filter-actions">
          <button
            class="action-btn-compact"
            :disabled="isLoading"
            @click="refreshOrders"
          >
            <feather-icon
              icon="RefreshCwIcon"
              size="16"
            />
          </button>
          <button
            class="action-btn-compact"
            :disabled="isLoading"
            @click="clearFilters"
          >
            <feather-icon
              icon="XIcon"
              size="16"
            />
          </button>
        </div>
      </div>
    </div>

    <!-- Action Row -->
    <div class="action-row-compact mb-3">
      <div class="selection-info-compact">
        <div class="selection-buttons">
          <button
            class="selection-btn"
            :disabled="isLoading || filteredOrders.length === 0"
            @click="selectAllVisible"
          >
            <feather-icon
              icon="CheckSquareIcon"
              size="14"
              class="mr-1"
            />
            Tout sélectionner
            <span class="count-badge">{{ filteredOrders.length }}</span>
          </button>
          <button
            class="selection-btn secondary"
            :disabled="selectedOrders.selectedRows.length === 0"
            @click="clearSelection"
          >
            <feather-icon
              icon="XIcon"
              size="14"
              class="mr-1"
            />
            Désélectionner
          </button>
        </div>
        <span class="selection-count-text">
          {{ selectedOrders.selectedRows.length > 0 ? selectedOrders.selectedRows.length + ' sélectionnée(s)' : 'Aucune sélection' }}
        </span>
      </div>

      <div class="create-section-compact">
        <div
          v-if="selectedOrders.selectedRows.length === 0"
          class="empty-selection-text"
        >
          <feather-icon
            icon="AlertCircleIcon"
            size="14"
            class="mr-1"
          />
          Sélectionnez des expéditions pour créer une collecte
        </div>
        <button
          class="create-btn-compact"
          :disabled="selectedOrders.selectedRows.length === 0 || isLoading"
          @click="addPickup"
        >
          <feather-icon
            icon="PackageIcon"
            size="16"
            :class="{ 'spinning': isLoading }"
          />
          {{ isLoading ? 'Création...' : 'Créer la collecte' }}
        </button>
      </div>
    </div>

    <!-- Selection Summary -->
    <div
      v-if="selectedOrders.selectedRows.length > 0"
      class="mb-3"
    >
      <b-card class="brand-selection-summary">
        <div class="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center">
          <div class="d-flex flex-wrap align-items-center mb-2 mb-md-0">
            <feather-icon
              icon="InfoIcon"
              class="mr-2 text-primary"
            />
            <strong>{{ selectedOrders.selectedRows.length }}</strong>
            <span class="ml-1 mr-3">expédition(s) sélectionnée(s)</span>
          </div>
          <div>
            <small class="text-muted">
              Montant total : <strong class="text-success">{{ calculateSelectedTotal() }} TND</strong>
            </small>
          </div>
        </div>
      </b-card>
    </div>

    <!-- Loading State -->
    <div
      v-if="isLoading"
      class="text-center mb-4"
    >
      <div class="brand-loading-container">
        <b-spinner
          variant="primary"
          class="brand-spinner"
        />
        <p class="brand-loading-text mt-2">
          Chargement des expéditions...
        </p>
      </div>
    </div>

    <!-- Enhanced Table - Only show if there are orders to display -->
    <b-card
      v-if="!isLoading && filteredOrders.length > 0"
      class="brand-table-card"
    >
      <template #header>
        <div class="d-flex flex-column flex-md-row align-items-start align-items-md-center justify-content-between">
          <h6 class="brand-section-title mb-2 mb-md-0">
            <feather-icon
              icon="ListIcon"
              class="mr-2"
            />
            Vos expéditions disponibles
          </h6>
          <div class="d-flex flex-wrap align-items-center ml-1">
            <b-badge
              class="brand-badge brand-badge-custom mr-2 mb-1 mb-md-0"
            >
              {{ filteredOrders.length }} expédition(s) trouvée(s)
            </b-badge>
            <small class="text-muted">
              ({{ selectedOrders.selectedRows.length }} expédition(s) sélectionnée(s))
            </small>
          </div>
        </div>
      </template>

      <vue-good-table
        ref="my-table"
        :columns="columns"
        :rows="filteredOrders"
        :search-options="{
          enabled: true,
          externalQuery: searchTerm,
        }"
        :select-options="{
          enabled: true,
          selectOnCheckboxOnly: true,
          selectionInfoClass: 'brand-selection-info',
          selectionText: 'expéditions sélectionnées',
          clearSelectionText: 'Tout désélectionner',
          disableSelectInfo: false,
          selectAllByGroup: true,
        }"
        :pagination-options="{
          enabled: true,
          perPage: pageLength,
          perPageDropdown: [50, 100, 500],
        }"
        class="brand-table"
        @on-selected-rows-change="selectionChanged"
      >
        <template
          slot="table-row"
          slot-scope="props"
        >
          <!-- BL Column -->
          <div
            v-if="props.column.field === 'id'"
            class="brand-cell-primary"
          >
            <strong class="text-primary">#{{ props.row.display_cross_tenant_barcode && props.row.cross_tenant_barcode ? props.row.cross_tenant_barcode : props.row.id }}</strong>
          </div>

          <!-- Client Column -->
          <div
            v-else-if="props.column.field === 'name'"
            class="brand-cell-customer"
          >
            <div class="font-weight-medium">
              {{ props.formattedRow[props.column.field] }}
            </div>
            <small class="text-muted d-block">{{ props.row.phone1 }}</small>
          </div>

          <!-- Gouvernorat Column -->
          <div
            v-else-if="props.column.field === 'governorate'"
            class="brand-cell-location"
          >
            <feather-icon
              icon="MapPinIcon"
              size="14"
              class="mr-1 text-muted"
            />
            {{ props.formattedRow[props.column.field] }}
          </div>

          <!-- Price Column -->
          <div
            v-else-if="props.column.field === 'price_ttc'"
            class="brand-cell-price"
          >
            <strong class="text-success">{{ formatPrice(props.formattedRow[props.column.field]) }} TND</strong>
          </div>

          <!-- Date Column -->
          <div
            v-else-if="props.column.field === 'created_at'"
            class="brand-cell-date"
          >
            <small class="text-muted">{{ props.formattedRow[props.column.field] }}</small>
          </div>

          <!-- Default -->
          <span
            v-else
            class="brand-cell-default"
          >
            {{ props.formattedRow[props.column.field] }}
          </span>
        </template>
      </vue-good-table>
    </b-card>

    <!-- No Orders State - Enhanced with better conditional rendering -->
    <div
      v-if="!isLoading && orders.length === 0"
      class="text-center py-5"
    >
      <b-card class="brand-empty-state">
        <feather-icon
          icon="PackageIcon"
          size="48"
          class="text-muted mb-3"
        />
        <h5 class="text-muted mb-3">
          Aucune commande disponible
        </h5>
        <p class="text-muted mb-4">
          <span v-if="date">
            Aucune commande trouvée pour la date sélectionnée.
            <br>
            Essayez de changer la date ou supprimez le filtre de date.
          </span>
          <span v-else>
            Vous n'avez actuellement aucune commande disponible pour créer un pickup.
          </span>
        </p>
        <div class="d-flex flex-column flex-md-row justify-content-center align-items-center">
          <b-button
            v-if="date"
            variant="outline-primary"
            class="mb-2 mb-md-0 mr-md-2"
            @click="clearDateFilter"
          >
            <feather-icon
              icon="CalendarIcon"
              size="16"
              class="mr-1"
            />
            Voir toutes les commandes
          </b-button>
          <b-button
            variant="outline-secondary"
            @click="refreshOrders"
          >
            <feather-icon
              icon="RefreshCwIcon"
              size="16"
              class="mr-1"
            />
            Actualiser
          </b-button>
        </div>
      </b-card>
    </div>

    <!-- Orders found but filtered out -->
    <div
      v-if="!isLoading && orders.length > 0 && filteredOrders.length === 0"
      class="text-center py-5"
    >
      <b-card class="brand-filtered-state">
        <feather-icon
          icon="FilterIcon"
          size="48"
          class="text-muted mb-3"
        />
        <h5 class="text-muted mb-3">
          Aucune commande ne correspond aux filtres
        </h5>
        <p class="text-muted mb-4">
          {{ orders.length }} commande(s) disponible(s), mais aucune ne correspond aux critères de filtrage actuels.
        </p>
        <div class="d-flex flex-column flex-md-row justify-content-center align-items-center">
          <b-button
            variant="outline-primary"
            class="mb-2 mb-md-0 mr-md-2"
            @click="clearGovernorateFilter"
          >
            <feather-icon
              icon="FilterIcon"
              size="16"
              class="mr-1"
            />
            Supprimer les filtres
          </b-button>
          <b-button
            variant="outline-info"
            @click="showAllOrders"
          >
            <feather-icon
              icon="EyeIcon"
              size="16"
              class="mr-1"
            />
            Voir toutes ({{ orders.length }})
          </b-button>
        </div>
      </b-card>
    </div>
  </div>
</template>

<script>
import {
  BFormInput,
  BButton,
  BSpinner,
  BFormDatepicker,
  BCard,
  BBadge,
} from 'bootstrap-vue'
import { VueGoodTable } from 'vue-good-table'
import vSelect from 'vue-select'
import axios from 'axios'
import { orderReferenceContains } from '@/utils/orderReference'
import storeAuth from '@/store/store'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'

export default {
  name: 'CreatePickup',
  components: {
    VueGoodTable,
    BFormInput,
    BButton,
    BSpinner,
    BFormDatepicker,
    BCard,
    BBadge,
    vSelect,
  },
  data() {
    return {
      date: null,
      pageLength: 100,
      selectedGovernorate: null,

      // Date picker configuration
      datePickerConfig: {
        locale: 'fr-FR',
        startWeekday: 1, // Monday
        labelHelp: 'Utilisez les flèches pour naviguer',
        labelPrevYear: 'Année précédente',
        labelPrevMonth: 'Mois précédent',
        labelCurrentMonth: 'Mois actuel',
        labelNextMonth: 'Mois suivant',
        labelNextYear: 'Année suivante',
        labelToday: 'Aujourd\'hui',
        labelSelected: 'Date sélectionnée',
        labelNoDateSelected: 'Aucune date sélectionnée',
        labelCalendar: 'Calendrier',
        labelNav: 'Navigation du calendrier',
      },

      columns: [
        {
          label: 'BL',
          field: 'id',
          width: '100px',
          filterOptions: {
            enabled: true,
            placeholder: 'Chercher BL',
            filterFn(data, filterString, row) {
              const search = filterString.toLowerCase()
              return orderReferenceContains(row, search)
            },
          },
        },
        {
          label: 'Client',
          field: 'name',
          width: '200px',
          filterOptions: {
            enabled: true,
            placeholder: 'Chercher client',
          },
        },
        {
          label: 'Gouvernorat',
          field: 'governorate',
          width: '130px',
          filterOptions: {
            enabled: true,
            placeholder: 'Chercher gouvernorat',
          },
        },
        {
          label: 'Prix TTC',
          field: 'price_ttc',
          width: '120px',
          type: 'number',
          filterOptions: {
            enabled: true,
            placeholder: 'Chercher prix',
          },
        },
        {
          label: 'Date création',
          field: 'created_at',
          width: '140px',
          formatFn: this.formatFn,
          filterOptions: {
            enabled: true,
            placeholder: 'Recherche date',
          },
        },
      ],
      user: {},
      orders: [],
      filteredOrders: [],
      governorates: [],
      searchTerm: '',
      isLoading: false,
      selectedOrders: {
        selectedRows: [],
      },

      // Add datetime for pickup scheduling
      pickupDatetime: null,
    }
  },
  computed: {
    availableOrders() {
      let filtered = [...this.orders]

      // Filter by governorate
      if (this.selectedGovernorate) {
        filtered = filtered.filter(order => order.governorate === this.selectedGovernorate.name)
      }

      return filtered
    },
  },
  created() {
    this.getGovernorates()
    this.user = storeAuth.state.user
    this.role = localStorage.getItem('role')
    this.getOrders()
  },
  methods: {
    formatFn(date) {
      if (!date) {
        return null
      }
      const [year, month, day] = date.split('-')
      const day1 = day.substr(0, 2)
      const h = date.slice(11, 19)
      return `${year}-${month.padStart(2, '0')}-${day1.padStart(2, '0')} | ${h}`
    },
    /**
     * Format date for API (ensure YYYY-MM-DD format)
     */
    formatDateForAPI(date) {
      if (!date) return ''
      // Bootstrap Vue datepicker returns date in YYYY-MM-DD format
      return date
    },
    async getOrders() {
      this.isLoading = true
      try {
        let url = '/api/orders/eligible-for-pickup/'

        // If date is selected, add it as a query parameter for filtering orders by creation date
        if (this.date) {
          const formattedDate = this.formatDateForAPI(this.date)
          url += `?date=${formattedDate}`
        }

        const { data } = await axios.get(url)
        this.orders = data
        this.filteredOrders = [...this.orders]
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('[getOrders] error:', error?.response?.status, error?.response?.data || error?.message)
        this.showToast('danger', 'top-center', 'Erreur lors du chargement des commandes')
      } finally {
        this.isLoading = false
      }
    },
    async getGovernorates() {
      try {
        const { data } = await axios.get('/api/governorates/')
        this.governorates = data
      } catch (error) {
        this.showToast('danger', 'top-center', error.toString())
      }
    },
    async addPickup() {
      try {
        this.isLoading = true
        const selectedOrders = this.$refs['my-table'].selectedRows
        const orderIds = selectedOrders.map(order => String(order.id).trim())

        if (orderIds.some(id => !id)) {
          const invalidOrders = selectedOrders.filter(order => !String(order.id).trim())
          const invalidLabels = invalidOrders.map(order => order.name || 'Commande sans client')
          const invalidMessage = invalidLabels.length
            ? `Identifiants invalides pour: ${invalidLabels.join(', ')}`
            : 'Certaines expéditions sélectionnées ont un identifiant invalide.'
          this.showToast('warning', 'top-center', invalidMessage)
          this.isLoading = false
          return
        }

        const pickupData = {
          order_ids: orderIds,
        }

        // Add pickup datetime if selected
        if (this.pickupDatetime) {
          pickupData.pickup_datetime = new Date(this.pickupDatetime).toISOString()
        }

        const { data } = await axios.post('/api/pickups/create-pickup/', pickupData)
        const pickupId = data.id

        this.showToast('success', 'top-center', 'Pickup ajouté avec succès')
        this.isLoading = false
        this.$router.push(`/pickups/show/${pickupId}`)
      } catch (error) {
        this.showToast('danger', 'top-center', error.toString())
        this.isLoading = false
      }
    },
    showToast(variant, position, text) {
      this.$toast(
        {
          component: ToastificationContent,
          props: {
            title: 'Notification',
            icon: 'InfoIcon',
            text,
            variant,
          },
        },
        {
          position,
        },
      )
    },
    selectionChanged(selectedRows) {
      this.selectedOrders = selectedRows
    },
    formatPrice(price) {
      return parseFloat(price || 0).toFixed(3)
    },
    refreshOrders() {
      this.getOrders()
    },

    // New methods for filtering and selection
    filterByGovernorate() {
      this.applyFilters()
    },

    applyFilters() {
      let filtered = [...this.orders]

      // Filter by governorate
      if (this.selectedGovernorate) {
        filtered = filtered.filter(order => order.governorate === this.selectedGovernorate.name)
      }

      this.filteredOrders = filtered
    },

    clearFilters() {
      this.selectedGovernorate = null
      this.date = null
      this.searchTerm = ''
      this.filteredOrders = [...this.orders]
      this.getOrders()
    },

    clearDateFilter() {
      this.date = null
      this.getOrders()
    },

    selectAllVisible() {
      if (this.$refs['my-table']) {
        this.$refs['my-table'].selectAll()
      }
    },

    clearSelection() {
      if (this.$refs['my-table']) {
        this.$refs['my-table'].clearSelected()
      }
    },

    calculateSelectedTotal() {
      const total = this.selectedOrders.selectedRows.reduce((sum, order) => sum + parseFloat(order.price_ttc || 0), 0)
      return total.toFixed(3)
    },

    clearGovernorateFilter() {
      this.selectedGovernorate = null
      this.applyFilters()
    },

    showAllOrders() {
      this.selectedGovernorate = null
      this.searchTerm = ''
      this.filteredOrders = [...this.orders]
    },

    calculateAverageOrder() {
      if (this.selectedOrders.selectedRows.length === 0) return '0.000'
      const total = this.selectedOrders.selectedRows.reduce((sum, order) => sum + parseFloat(order.price_ttc || 0), 0)
      return (total / this.selectedOrders.selectedRows.length).toFixed(3)
    },

    showSelectionDetails() {
      // Show modal or detailed view of selected orders
      const details = this.selectedOrders.selectedRows.map(order => ({
        id: order.id,
        client: order.name,
        amount: order.price_ttc,
        governorate: order.governorate,
      }))

      this.showToast('info', 'top-center', `Détails: ${details.length} commandes sélectionnées`)
    },
  },
}
</script>

<style lang="scss">
@import url('https://fonts.googleapis.com/css2?family=Poppins:wght@300;400;500;600;700&display=swap');
@import "@core/scss/vue/libs/vue-good-table.scss";
@import "@core/scss/vue/libs/vue-select.scss";

// Brand Variables
$brand-black: #090909;
$brand-red: #0A2634;

.pickup-create-container {
  font-family: 'Poppins', sans-serif;

  // Custom Badge Styling
  .brand-badge-custom {
    background: rgba(37, 99, 235, 0.12) !important;
    background-color: rgba(37, 99, 235, 0.12) !important;
    color: #2563eb !important;
    font-weight: 600;
    padding: 0.5rem 1rem;
    border-radius: 6px;
    border: none !important;
  }

  // Header Card
  .app-header.small-header {
    border: none;
    box-shadow: 0 4px 25px 0 rgba(10, 38, 52, 0.1);
    border-radius: 16px;

    .card-header {
      background: linear-gradient(135deg, rgba(10, 38, 52, 0.05) 0%, rgba(29, 29, 27, 0.02) 100%);
      border-bottom: none;
      border-radius: 16px 16px 0 0 !important;
      padding: 1.5rem 2rem;
    }
  }

  .brand-page-title {
    color: $brand-black;
    font-weight: 700;
    font-size: 1.5rem;
    margin: 0;
  }

  .brand-page-subtitle {
    color: lighten($brand-black, 30%);
    font-weight: 400;
    font-size: 0.95rem;
  }

  // Compact Filters Panel
  .filters-panel-compact {
    background: white;
    border-radius: 10px;
    padding: 1rem 1.25rem;
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);
    border: 1px solid #e5e7eb;
  }

  .filters-row {
    display: flex;
    flex-wrap: wrap;
    align-items: flex-end;
    gap: 0.75rem;
  }

  .filter-item {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
    min-width: 140px;
    flex: 1;
  }

  .filter-item-wide {
    flex: 2;
    min-width: 200px;
  }

  .filter-label-compact {
    font-size: 0.7rem;
    font-weight: 500;
    color: #6b7280;
    text-transform: uppercase;
    letter-spacing: 0.3px;
  }

  .filter-input-compact {
    height: 36px !important;
    min-height: 36px !important;
    font-size: 0.85rem !important;
    border: 1px solid #e5e7eb !important;
    border-radius: 6px !important;
    padding: 0.4rem 0.75rem !important;
    transition: all 0.2s ease !important;

    &:focus {
      border-color: #2563eb !important;
      box-shadow: 0 0 0 2px rgba(37, 99, 235, 0.12) !important;
    }
  }

  .filter-select-compact {
    .vs__dropdown-toggle {
      height: 36px !important;
      min-height: 36px !important;
      border: 1px solid #e5e7eb !important;
      border-radius: 6px !important;
      padding: 0 0.5rem !important;
    }

    .vs__search {
      font-size: 0.85rem !important;
      margin: 0 !important;
      padding: 0 !important;
    }

    .vs__selected {
      font-size: 0.85rem !important;
      margin: 0 !important;
    }
  }

  .filter-actions {
    display: flex;
    gap: 0.5rem;
  }

  .action-btn-compact {
    width: 36px;
    height: 36px;
    border: 1px solid #e5e7eb;
    background: white;
    border-radius: 6px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: all 0.2s ease;
    color: #6b7280;

    &:hover:not(:disabled) {
      border-color: #2563eb;
      color: #2563eb;
      background: rgba(37, 99, 235, 0.08);
    }

    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
  }

  // Action Row Compact
  .action-row-compact {
    background: white;
    border-radius: 10px;
    padding: 0.75rem 1.25rem;
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);
    border: 1px solid #e5e7eb;
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-wrap: wrap;
    gap: 1rem;
  }

  .selection-info-compact {
    display: flex;
    align-items: center;
    gap: 1rem;
    flex-wrap: wrap;
  }

  .selection-buttons {
    display: flex;
    gap: 0.5rem;
  }

  .selection-btn {
    display: flex;
    align-items: center;
    gap: 0.25rem;
    padding: 0.4rem 0.75rem;
    font-size: 0.8rem;
    font-weight: 500;
    border: 1px solid #e5e7eb;
    background: white;
    border-radius: 6px;
    cursor: pointer;
    transition: all 0.2s ease;
    color: #374151;

    &:hover:not(:disabled) {
      border-color: #2563eb;
      color: #2563eb;
    }

    &.secondary {
      color: #6b7280;
    }

    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }

    .count-badge {
      background: rgba(37, 99, 235, 0.12);
      color: #2563eb;
      padding: 0.1rem 0.4rem;
      border-radius: 4px;
      font-size: 0.7rem;
      font-weight: 600;
    }
  }

  .selection-count-text {
    font-size: 0.8rem;
    color: #6b7280;
  }

  .create-section-compact {
    display: flex;
    align-items: center;
    gap: 1rem;
  }

  .empty-selection-text {
    display: flex;
    align-items: center;
    font-size: 0.8rem;
    color: #9ca3af;
  }

  .create-btn-compact {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.5rem 1rem;
    font-size: 0.85rem;
    font-weight: 600;
    background: #2563eb;
    color: white;
    border: none;
    border-radius: 8px;
    cursor: pointer;
    transition: all 0.2s ease;
    box-shadow: 0 2px 6px rgba(37, 99, 235, 0.25);

    &:hover:not(:disabled) {
      background: #1d4ed8;
      transform: translateY(-1px);
      box-shadow: 0 4px 10px rgba(37, 99, 235, 0.35);
    }

    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }

    .spinning {
      animation: spin 1s linear infinite;
    }
  }

  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }

  // Responsive
  @media (max-width: 768px) {
    .filters-row {
      flex-direction: column;
    }

    .filter-item,
    .filter-item-wide {
      width: 100%;
      min-width: 100%;
    }

    .filter-actions {
      width: 100%;
      justify-content: flex-end;
    }

    .action-row-compact {
      flex-direction: column;
      align-items: stretch;
    }

    .selection-info-compact {
      justify-content: space-between;
    }

    .create-section-compact {
      justify-content: flex-end;
    }
  }

  .brand-section-title {
    color: $brand-black;
    font-weight: 600;
    font-size: 1.1rem;
    margin: 0;
  }

  // Table Card
  .brand-table-card {
    border: none;
    border-radius: 12px;
    box-shadow: 0 4px 25px 0 rgba(29, 29, 27, 0.1);

    .card-header {
      background: rgba(29, 29, 27, 0.02);
      border-bottom: 2px solid rgba(10, 38, 52, 0.1);
      border-radius: 12px 12px 0 0 !important;
      padding: 1rem 1.5rem;

      @media (max-width: 768px) {
        padding: 1rem;
      }
    }

    .card-body {
      padding: 0;
      overflow-x: auto;
    }
  }

  // Consistent input sizing
  .brand-input-consistent {
    min-height: 48px !important;

    // For regular inputs
    &.form-control {
      height: 48px !important;
      padding: 0.75rem 1rem !important;
    }

    // For datepicker
    &.brand-datepicker .form-control {
      height: 48px !important;
      min-height: 48px !important;
      padding: 0.75rem 1rem !important;
    }

    // For v-select
    &.brand-select .vs__dropdown-toggle {
      min-height: 48px !important;
      padding: 0.5rem 0.75rem !important;
    }
  }

  // Action group styling
  .brand-action-group {
    margin-bottom: 1rem;

    .brand-btn-action {
      min-height: 48px !important;
      width: 48px !important;
      padding: 0.75rem !important;
      display: flex !important;
      align-items: center !important;
      justify-content: center !important;
    }
  }

  // Enhanced responsive form groups
  .brand-form-group {
    margin-bottom: 1rem;

    label {
      color: $brand-black;
      font-weight: 500;
      font-size: 0.9rem;
      margin-bottom: 0.5rem;
      text-transform: uppercase;
      letter-spacing: 0.5px;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    @media (max-width: 768px) {
      margin-bottom: 1.5rem;

      label {
        font-size: 0.85rem;
      }
    }
  }

  // Datepicker styling improvements
  .brand-datepicker {
    .form-control {
      border: 2px solid #e3e6f0 !important;
      border-radius: 8px !important;
      font-family: 'Poppins', sans-serif !important;
      background-color: #fff !important;
      background-image: none !important;
      transition: all 0.3s ease !important;

      &:focus {
        border-color: $brand-red !important;
        box-shadow: 0 0 0 0.2rem rgba(10, 38, 52, 0.15) !important;
        outline: none !important;
      }

      &::placeholder {
        color: #6c757d !important;
        opacity: 1 !important;
      }
    }

    // Calendar button
    .btn {
      border-color: #e3e6f0 !important;
      height: 48px !important;

      &:hover {
        border-color: $brand-red !important;
        background-color: rgba(10, 38, 52, 0.1) !important;
      }
    }

    // Responsive adjustments
    @media (max-width: 768px) {
      .form-control {
        font-size: 16px !important; // Prevent zoom on iOS
      }
    }
  }

  // Enhanced v-select styling with consistent sizing
  .brand-select {
    &.brand-input-consistent .vs__dropdown-toggle {
      border: 2px solid #e3e6f0 !important;
      border-radius: 8px !important;
      min-height: 48px !important;
      padding: 0.5rem 0.75rem !important;
      font-family: 'Poppins', sans-serif !important;
      background-color: #fff !important;
      transition: all 0.3s ease !important;

      &:hover {
        border-color: lighten($brand-red, 20%) !important;
      }
    }

    .vs__selected {
      color: $brand-black !important;
      font-weight: 500 !important;
      font-family: 'Poppins', sans-serif !important;
      line-height: 1.5 !important;
    }

    .vs__dropdown-menu {
      border: 2px solid rgba(10, 38, 52, 0.1) !important;
      border-radius: 8px !important;
      box-shadow: 0 4px 25px 0 rgba(29, 29, 27, 0.15) !important;
      font-family: 'Poppins', sans-serif !important;
    }

    .vs__dropdown-option--highlight {
      background-color: rgba(10, 38, 52, 0.1) !important;
      color: $brand-black !important;
    }

    .vs__search {
      font-family: 'Poppins', sans-serif !important;
      padding: 0.25rem 0 !important;
    }

    .vs__clear {
      color: lighten($brand-black, 40%) !important;

      &:hover {
        color: $brand-red !important;
      }
    }

    .vs__open-indicator {
      color: lighten($brand-black, 40%) !important;
    }

    &.vs--open .vs__dropdown-toggle {
      border-color: $brand-red !important;
      box-shadow: 0 0 0 0.2rem rgba(10, 38, 52, 0.15) !important;
    }

    // Mobile responsive
    @media (max-width: 768px) {
      .vs__dropdown-toggle {
        min-height: 44px !important;
      }
    }
  }

  // Enhanced empty states
  .brand-empty-state,
  .brand-filtered-state {
    border: 2px dashed rgba(10, 38, 52, 0.2);
    background: rgba(10, 38, 52, 0.02);
    border-radius: 12px;

    .card-body {
      padding: 3rem 2rem;
    }

    .feather-icon {
      opacity: 0.5;
    }

    @media (max-width: 768px) {
      .card-body {
        padding: 2rem 1rem;
      }
    }
  }

  .brand-filtered-state {
    border-color: rgba(23, 162, 184, 0.3);
    background: rgba(23, 162, 184, 0.03);
  }

  // Enhanced selection summary responsive
  .brand-selection-summary {
    background: linear-gradient(135deg, rgba(10, 38, 52, 0.05) 0%, rgba(29, 29, 27, 0.02) 100%);
    border: 2px solid rgba(10, 38, 52, 0.1);
    border-radius: 8px;

    .card-body {
      padding: 1rem 1.5rem;
    }

    @media (max-width: 768px) {
      .card-body {
        padding: 1rem;
      }
    }
  }

  // Enhanced table responsiveness
  .brand-table-card {
    border: none;
    border-radius: 12px;
    box-shadow: 0 4px 25px 0 rgba(29, 29, 27, 0.1);

    .card-header {
      background: rgba(29, 29, 27, 0.02);
      border-bottom: 2px solid rgba(10, 38, 52, 0.1);
      border-radius: 12px 12px 0 0 !important;
      padding: 1rem 1.5rem;

      @media (max-width: 768px) {
        padding: 1rem;
      }
    }

    .card-body {
      padding: 0;
      overflow-x: auto;
    }
  }

  // Enhanced cell styling
  .brand-cell-customer {
    .font-weight-medium {
      font-weight: 500 !important;
      color: $brand-black !important;
      font-family: 'Poppins', sans-serif !important;
    }

    small {
      font-size: 0.75rem !important;
    }
  }

  .brand-cell-location {
    display: flex;
    align-items: center;
    color: lighten($brand-black, 20%) !important;
    font-weight: 500 !important;
    font-family: 'Poppins', sans-serif !important;
  }

  // Enhanced Action Row
  .brand-action-row {
    border-top: 2px solid rgba(10, 38, 52, 0.1);
    margin-top: 1.5rem !important;
    padding-top: 1.5rem !important;
    background: linear-gradient(135deg, rgba(10, 38, 52, 0.02) 0%, rgba(29, 29, 27, 0.01) 100%);
    border-radius: 12px;
    margin-left: -1rem;
    margin-right: -1rem;
    padding-left: 1rem;
    padding-right: 1rem;
  }

  .brand-action-container {
    display: flex;
    flex-direction: column;
    gap: 1.5rem;

    @media (min-width: 992px) {
      flex-direction: row;
      align-items: flex-start;
      justify-content: space-between;
      gap: 2rem;
    }
  }

  // Selection Section
  .brand-selection-section {
    flex: 1;
    min-width: 0;

    .brand-action-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      margin-bottom: 1rem;
      padding-bottom: 0.75rem;
      border-bottom: 1px solid rgba(10, 38, 52, 0.1);

      .brand-action-title {
        display: flex;
        align-items: center;
        color: $brand-black;
        font-size: 0.95rem;
        font-weight: 600;
        font-family: 'Poppins', sans-serif;
      }

      .brand-selection-count {
        display: flex;
        align-items: center;
        font-size: 0.85rem;

        .brand-count-indicator {
          background: linear-gradient(135deg, $brand-red 0%, darken($brand-red, 10%) 100%);
          font-size: 0.75rem;
          padding: 0.3rem 0.6rem;
          font-weight: 600;
        }
      }
    }

    .brand-action-buttons-group {
      display: flex;
      flex-direction: column;
      gap: 1rem;

      @media (min-width: 768px) {
        flex-direction: row;
        align-items: flex-start;
        justify-content: space-between;
      }
    }

    .brand-selection-buttons {
      display: flex;
      flex-wrap: wrap;
      gap: 0.5rem;
    }
  }

  // Enhanced Selection Buttons
  .brand-btn-selection {
    border: 2px solid #e8ecf4 !important;
    background-color: #fff !important;
    border-radius: 8px !important;
    padding: 0.5rem 1rem !important;
    font-weight: 500 !important;
    font-family: 'Poppins', sans-serif !important;
    transition: all 0.3s ease !important;
    height: 38px !important;
    display: flex !important;
    align-items: center !important;
    white-space: nowrap !important;
    font-size: 0.85rem !important;

    &:hover:not(:disabled) {
      border-color: $brand-red !important;
      color: $brand-red !important;
      background-color: rgba(10, 38, 52, 0.05) !important;
      transform: translateY(-1px) !important;
      box-shadow: 0 2px 8px rgba(10, 38, 52, 0.15) !important;
    }

    &.btn-outline-primary {
      border-color: $brand-red !important;
      color: $brand-red !important;

      &:hover:not(:disabled) {
        background-color: $brand-red !important;
        color: #fff !important;

        .brand-mini-badge {
          background-color: rgba(255, 255, 255, 0.2) !important;
          color: #fff !important;
        }
      }
    }

    &:disabled {
      opacity: 0.6 !important;
      cursor: not-allowed !important;
      transform: none !important;
    }

    .brand-mini-badge {
      background-color: rgba(10, 38, 52, 0.1) !important;
      color: $brand-red !important;
      font-size: 0.7rem !important;
      padding: 0.15rem 0.3rem !important;
      border-radius: 4px !important;
      font-weight: 600 !important;
    }
  }

  // Quick Stats
  .brand-quick-stats {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    padding: 0.75rem;
    background: rgba(10, 38, 52, 0.03);
    border: 1px solid rgba(10, 38, 52, 0.1);
    border-radius: 8px;
    min-width: 200px;

    @media (max-width: 767px) {
      flex-direction: row;
      justify-content: space-around;
    }

    .brand-stat-item {
      display: flex;
      justify-content: space-between;
      align-items: center;
      font-size: 0.85rem;

      .brand-stat-label {
        color: lighten($brand-black, 30%);
        font-weight: 500;
      }

      .brand-stat-value {
        font-family: 'Poppins', sans-serif;
        font-size: 0.9rem;
      }
    }
  }

  // Create Section
  .brand-create-section {
    display: flex;
    flex-direction: column;
    align-items: stretch;
    min-width: 280px;

    @media (min-width: 992px) {
      align-items: flex-end;
      min-width: 320px;
    }

    .brand-create-info {
      margin-bottom: 1rem;
      text-align: center;

      @media (min-width: 992px) {
        text-align: right;
      }

      .brand-empty-selection,
      .brand-ready-info {
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 0.5rem;
        border-radius: 6px;
        font-size: 0.85rem;

        @media (min-width: 992px) {
          justify-content: flex-end;
        }
      }

      .brand-empty-selection {
        background: rgba(255, 193, 7, 0.1);
        border: 1px solid rgba(255, 193, 7, 0.2);
      }

      .brand-ready-info {
        background: rgba(40, 167, 69, 0.1);
        border: 1px solid rgba(40, 167, 69, 0.2);
      }
    }
  }

  // Enhanced Create Pickup Button
  .brand-btn-create-pickup {
    background: linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%) !important;
    border: none !important;
    border-radius: 12px !important;
    padding: 0 !important;
    font-weight: 600 !important;
    text-transform: uppercase !important;
    letter-spacing: 0.5px !important;
    transition: all 0.3s ease !important;
    box-shadow: 0 4px 20px 0 rgba(10, 38, 52, 0.3) !important;
    font-family: 'Poppins', sans-serif !important;
    min-height: 60px !important;
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
    position: relative !important;
    overflow: hidden !important;

    &:hover:not(:disabled) {
      transform: translateY(-2px) !important;
      box-shadow: 0 6px 25px 0 rgba(37, 99, 235, 0.4) !important;
      background: linear-gradient(135deg, #1d4ed8 0%, #1e40af 100%) !important;
    }

    &:disabled {
      opacity: 0.6 !important;
      cursor: not-allowed !important;
      transform: none !important;
    }

    .brand-btn-content {
      display: flex;
      align-items: center;
      gap: 0.75rem;
      padding: 0.75rem 1.5rem;

      .feather-icon {
        flex-shrink: 0;

        &.spinning {
          animation: spin 1s linear infinite;
        }
      }

      .brand-btn-text {
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        gap: 0.2rem;

        .brand-btn-main {
          font-size: 0.95rem;
          font-weight: 600;
          line-height: 1;
        }

        .brand-btn-sub {
          font-size: 0.75rem;
          font-weight: 400;
          opacity: 0.9;
          text-transform: none;
          letter-spacing: normal;
          line-height: 1;
        }
      }
    }
  }

  // Responsive improvements
  @media (max-width: 991px) {
    .brand-action-container {
      .brand-create-section {
        .brand-btn-create-pickup {
          width: 100% !important;
          min-height: 56px !important;

          .brand-btn-content {
            .brand-btn-text {
              align-items: center;
              text-align: center;
            }
          }
        }
      }
    }
  }

  @media (max-width: 767px) {
    .brand-action-row {
      margin-left: -0.5rem;
      margin-right: -0.5rem;
      padding-left: 0.5rem;
      padding-right: 0.5rem;
    }

    .brand-action-container {
      gap: 1rem;
    }

    .brand-selection-section {
      .brand-action-header {
        flex-direction: column;
        align-items: flex-start;
        gap: 0.5rem;
      }

      .brand-action-buttons-group {
        flex-direction: column;
        gap: 0.75rem;
      }

      .brand-selection-buttons {
        justify-content: center;

        .brand-btn-selection {
          flex: 1;
          min-width: 0;
          max-width: none;
        }
      }
    }

    .brand-btn-create-pickup {
      .brand-btn-content {
        padding: 0.75rem 1rem !important;

        .brand-btn-text {
          .brand-btn-main {
            font-size: 0.9rem;
          }

          .brand-btn-sub {
            font-size: 0.7rem;
          }
        }
      }
    }
  }

  // Spinning animation for loading state
  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }

  // Enhanced datetime input styling (same as edit page)
  .brand-datetime-input {
    border: 2px solid #e3e6f0 !important;
    border-radius: 8px !important;
    min-height: 48px !important;
    padding: 0.75rem 1rem !important;
    font-size: 0.95rem !important;
    font-family: 'Poppins', sans-serif !important;
    transition: all 0.3s ease !important;
    background-color: #fff !important;
    color: $brand-black !important;

    &:focus {
      border-color: $brand-red !important;
      box-shadow: 0 0 0 0.2rem rgba(10, 38, 52, 0.15) !important;
      outline: none !important;
    }

    &:disabled {
      background-color: #f8f9fa !important;
      border-color: #dee2e6 !important;
      color: lighten($brand-black, 30%) !important;
      cursor: not-allowed !important;
    }

    // Custom datetime picker styling for webkit browsers
    &::-webkit-calendar-picker-indicator {
      background-image: url('data:image/svg+xml;charset=UTF-8,<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="%232563eb" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect><line x1="16" y1="2" x2="16" y2="6"></line><line x1="8" y1="2" x2="8" y2="6"></line><line x1="3" y1="10" x2="21" y2="10"></line></svg>');
      background-repeat: no-repeat;
      background-position: center;
      background-size: 16px;
      width: 20px;
      height: 20px;
      margin-left: 4px;
      cursor: pointer;

      &:hover {
        background-image: url('data:image/svg+xml;charset=UTF-8,<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="%231d4ed8" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect><line x1="16" y1="2" x2="16" y2="6"></line><line x1="8" y1="2" x2="8" y2="6"></line><line x1="3" y1="10" x2="21" y2="10"></line></svg>');
      }
    }

    // Mobile responsive adjustments
    @media (max-width: 768px) {
      font-size: 16px !important; // Prevent zoom on iOS
      padding: 0.875rem 1rem !important; // Slightly larger touch target
    }
  }
}
</style>
